// Angular
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  inject,
  input,
  OnInit,
  signal,
  DestroyRef,
  AfterViewInit,
  viewChild,
  ElementRef,
  output,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

// Angular Material
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';

// Third Party
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

// Components
import { LoadingSpinnerComponent, FormErrorComponent } from '@components/common-components';

// Services
import { IdentityProviderService } from '@services/identity-provider.service';

// Defs
import { CLIENT_AUTHENTICATION, IdentityProvider, IdentityProviderConfig, PROMPT } from '../defs';
import { environment } from '@env';
import { SuccessDialogComponent } from '@components/dialogs';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-customer-idp-form',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCardModule,
    MatSidenavModule,
    MatTooltipModule,
    RouterLink,
    NgxMatSelectSearchModule,
    NgClass,
    NgTemplateOutlet,
    LoadingSpinnerComponent,
    FormErrorComponent,
    MatIconModule,
  ],
  templateUrl: './customer-idp-form.component.html',
  styleUrl: './customer-idp-form.component.scss',
})
export class CustomerIdpFormComponent implements OnInit, AfterViewInit {
  // Inject
  private identityProviderService = inject(IdentityProviderService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);

  readonly focusTrap = viewChild<ElementRef>('focusTrap');

  // Signal
  readonly loading = signal<boolean>(true);
  readonly formPrepareError = signal<boolean>(false);
  readonly focusState = signal<Record<string, boolean>>({});
  readonly isBoxOpen = signal<boolean>(false);
  readonly redirectUri = signal<string>('');

  // Const
  readonly clientAuthOptions = Object.values(CLIENT_AUTHENTICATION);
  readonly promptOptions = Object.values(PROMPT);
  readonly redirectUrIPrefix = environment.idProvider + '/broker/';
  readonly redirectUrISufix = '/endpoint';

  // Input
  readonly customerKey = input<string>(null);

  // Output
  readonly formValid = output<boolean>();

  // State
  editIdp: boolean = false;
  secretBackUp: string = null;

  // Form
  readonly customerIdpForm = new FormGroup({
    alias: new FormControl(''),
    clientAuthentication: new FormControl(''),
    clientId: new FormControl('', [Validators.required]),
    clientSecret: new FormControl('', [Validators.required]),
    scopes: new FormControl(''),
    prompt: new FormControl(''),
    identityProviderConfigDTO: new FormGroup({
      discoveryUrl: new FormControl('', [Validators.required]),
      authorizationUrl: new FormControl('', [Validators.required]),
      tokenUrl: new FormControl('', [Validators.required]),
      logoutUrl: new FormControl('', [Validators.required]),
      userInfoUrl: new FormControl('', [Validators.required]),
      issuer: new FormControl('', [Validators.required]),
      jwksUrl: new FormControl('', [Validators.required]),
    }),
  });

  ngOnInit() {
    this.getCustomerIdpDetails(this.customerKey());
    this.customerIdpForm.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formValid.emit(this.customerIdpForm.valid);
    });
  }

  // Cursor was defaulting to field down the form
  // Could not default focus else where any other way
  ngAfterViewInit() {
    setTimeout(() => {
      this.focusTrap()?.nativeElement?.focus();
      this.customerIdpForm.get('clientId').markAsUntouched();
    }, 350);
  }

  getCustomerIdpDetails(customerKey: string) {
    this.identityProviderService
      .getIdp(customerKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: IdentityProvider) => {
          if (!data) {
            this.formPrepareError.set(true);
            return;
          }
          this.prepareForm(data);
        },
        error: () => {
          this.formPrepareError.set(true);
          this.loading.set(false);
        },
      });
  }

  prepareForm(data: IdentityProvider) {
    this.redirectUri.set(this.redirectUrIPrefix + data.alias + this.redirectUrISufix);
    this.customerIdpForm.patchValue({
      ...data,
      identityProviderConfigDTO: data.identityProviderConfigDTO,
    });
    this.customerIdpForm.get('identityProviderConfigDTO').disable();
    this.customerIdpForm.get('identityProviderConfigDTO.discoveryUrl').enable();

    if (data.clientSecret !== null) {
      this.editIdp = true;
      this.secretBackUp = data.clientSecret;
    } else {
      // Set default values
      this.customerIdpForm
        .get('clientAuthentication')
        .setValue(CLIENT_AUTHENTICATION.clientSecretPost.value);
      this.customerIdpForm.get('prompt').setValue(PROMPT.selectAccount.value);
      this.customerIdpForm.get('scopes').setValue('openid email profile');
    }
    this.loading.set(false);
  }

  // The submit button is outside this component's template.
  // The form value is easier accessed internally rather than passed as a parameter.
  onSubmit() {
    if (!this.customerIdpForm.valid) {
      this.customerIdpForm.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    // If scopes is null KC sets openid as default, represeting that on FE too
    if (this.customerIdpForm.get('scopes').value == '') {
      this.customerIdpForm.get('scopes').setValue('openid');
    }
    const identityProvider: IdentityProvider = this.customerIdpForm.getRawValue();

    const request = this.editIdp
      ? this.identityProviderService.updateIdp(identityProvider)
      : this.identityProviderService.createIdp(identityProvider);

    request
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.openSuccessDialog('You have successfully updated the Identity Provider.'),
        error: () => {},
      });
  }

  copyRedirectUri(tooltip: MatTooltip) {
    // Show tooltip for 1 second
    navigator.clipboard.writeText(this.redirectUri());
    tooltip.disabled = false;
    tooltip.show();
    setTimeout(() => {
      tooltip.hide();
      tooltip.disabled = true;
    }, 1000);
  }

  testConnection(url: string | null | undefined) {
    this.identityProviderService
      .testConnection(url)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: IdentityProviderConfig) => {
          this.openSuccessDialog('You have successfully established a connection.');
          this.customerIdpForm.get('identityProviderConfigDTO').patchValue(data);
        },
      });
  }

  openBox() {
    this.isBoxOpen.update((open) => !open);
  }

  onInputFocus(formValue: string) {
    this.focusState.update((state) => ({ ...state, [formValue]: true }));
  }

  onInputBlur(formValue: string) {
    this.focusState.update((state) => ({ ...state, [formValue]: false }));
  }

  // Secret comes back as *********
  // Ok to send back as same, will not overide existing
  // Can send back new string
  // If remove any * then clear all *
  // If blank on edit, set as original value
  blankSecret() {
    if (this.customerIdpForm.get('clientSecret').value === '' && this.secretBackUp !== null) {
      this.customerIdpForm.get('clientSecret').setValue(this.secretBackUp);
    }
  }

  editSecret() {
    if (this.customerIdpForm.get('clientSecret').value.includes('*')) {
      this.customerIdpForm.get('clientSecret').setValue('');
    }
  }

  trimField(controlName: string) {
    const control = this.customerIdpForm.get(controlName);
    if (control?.value) {
      control.setValue(control.value.trim());
    }
  }

  openSuccessDialog(text: string) {
    this.dialog.open(SuccessDialogComponent, {
      data: {
        title: `Success!`,
        message: text,
        button: 'Great!',
      },
    });
  }
}
